<script lang="ts">
  import { defineComponent } from 'vue';
  import { getStopLocations } from '@/api/transport';
  import { TrafiklabStops } from '@/types/api/transport';

  export default defineComponent({
    name: 'CreateTrafiklabWidgetModal',
  });
</script>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { debounce } from 'lodash';

  import Modal from '@/components/common/Modal.vue';

  import { Multiselect } from 'vue-multiselect';
  import { useToasted } from '@/helpers';

  const emits = defineEmits(['createWidget', 'closeModal']);

  const toasted = useToasted();

  const showModal = ref(false);
  const stopSearchKey = ref('');
  const trafiklabStops = ref<TrafiklabStops[]>([]);
  const selectedStop = ref<TrafiklabStops | null>(null);
  const isSaving = ref(false);
  const widgetName = ref('Trafiklab');
  const loading = ref(false);

  onMounted(() => {
    showModal.value = true;
  });

  const disableOkButton = computed(() => {
    return widgetName.value.length < 3 || !selectedStop.value;
  });

  const searchStop = debounce((searchValue = '') => {
    stopSearchKey.value = searchValue;

    if (!stopSearchKey.value) return;
    loading.value = true;

    getStopLocations(stopSearchKey.value)
      .then((stops: TrafiklabStops[]) => {
        trafiklabStops.value = stops.filter((stop) => !!stop.id);
      })
      .catch((err) => {
        toasted.global.general_error({ message: 'Failed to fetch the stops.' });
      })
      .finally(() => {
        loading.value = false;
      });
  }, 600);

  async function createTrafiklabWidget() {
    isSaving.value = true;

    const data = {
      name: widgetName.value,
      stop: selectedStop.value,
    };

    emits('createWidget', data);
    isSaving.value = false;
    closeModal();
  }

  function closeModal() {
    showModal.value = false;
    emits('closeModal');
  }
</script>

<template>
  <Modal
    @closeModal="closeModal"
    :isSaving="isSaving"
    title="Create Trafiklab Widget"
    :okFunction="createTrafiklabWidget"
    :modalStyles="{ width: '500px' }"
    :disabled="disableOkButton"
    avoidClickOutside
    noScrollbar
  >
    <template v-slot:body>
      <div class="group-body">
        <div class="form-group">
          <label for="widgetName">
            <div class="label">Widget Name</div>
            <input
              type="text"
              name="widgetName"
              id="widgetName"
              required
              minlength="3"
              maxlength="40"
              v-model="widgetName"
              placeholder="Set widget's name"
            />
          </label>
        </div>

        <div class="form-group">
          <BaseInput label="Stop Name">
            <Multiselect
              :limit="1"
              :options="trafiklabStops"
              :internal-search="false"
              track-by="name"
              label="name"
              v-model="selectedStop"
              placeholder="Search"
              :searchable="true"
              :allow-empty="false"
              open-direction="bottom"
              :loading="loading"
              :showLabels="false"
              @search-change="searchStop"
            >
              <template v-slot="{ option }">
                <div>{{ option.name }}</div>
              </template>
              <template #noResult>
                <span>No stops found</span>
              </template>
              <template #noOptions>
                <span>Enter a stop name</span>
              </template>
            </Multiselect>
          </BaseInput>
        </div>
      </div>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>
  .group-body {
    display: flex;
    gap: 20px;
    flex-direction: column;
    align-items: center;

    margin-right: -16px;
    padding-right: 16px;

    .form-group {
      width: 100%;
      margin-bottom: 0px;
      max-width: 480px;

      label {
        display: flex;
        flex-direction: column;
        margin-bottom: 0;
        gap: 8px;

        font-weight: 500;
        line-height: 24px;

        .label {
          display: flex;
          gap: 8px;
          align-items: center;

          font-size: 14px;
        }

        input,
        select {
          padding: 8px 16px;

          border: 2px solid $borderGrey;
          border-radius: 8px;
          font-size: 16px;
        }
      }
    }

    .description {
      grid-column: 1/3;
    }
  }

  ::v-deep {
    .multiselect {
      &__select {
        padding: 0;
        z-index: 501;
      }
      &__tags {
        border: 2px solid #e8e8e8;
        border-radius: 8px;
        font-size: 16px;
        padding: 6px 16px;
        .multiselect__input {
          padding: 0;
          margin: 0;
          font-size: 16px;
          padding-right: 26px;
          box-sizing: border-box;
          z-index: 500;
          border-radius: 0;
          height: 24px;
          &::placeholder {
            color: #95a6a6;
          }
        }
        .multiselect__placeholder {
          margin: 0;
          padding: 0;
          color: #95a6a6;
        }
      }

      &__content-wrapper {
        border-width: 2px;

        border-top-left-radius: 0;
        border-top-right-radius: 0;
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
      }

      &--above {
        .multiselect__content-wrapper {
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
      }

      &__single {
        margin: 0;
        padding: 0;
        line-height: 24px;
      }

      .multiselect__spinner {
        background: none;
        right: 25px;

        &:before,
        &:after {
          border-top-color: $primaryRed;
        }
      }
    }
  }
</style>
